import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  TrendingUp, Package, Gift, BarChart3, UserCog, Users,
  Check, X, Sparkles, ArrowUpRight, Zap, Send, MessageCircle
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ResultsSlider from "@/components/ResultsSlider";
import { useI18n } from "@/context/I18nContext";
import { HOME } from "@/constants/testIds";
import api, { formatApiError } from "@/lib/api";

const WHOP_PRO_URL = "https://whop.com/viralcrew-tiktok/viralcrew-pro/";
const WHOP_FREE_URL = "https://whop.com/viralcrew-tiktok/viralcrew-free/";
const WHOP_URL = WHOP_PRO_URL;
const WHOP_LOGO = "/assets/whop-logo.webp";
const WHATSAPP_NUMBER_RAW = "+49 152 23701174";
const WHATSAPP_URL = "https://wa.me/4915223701174?text=" + encodeURIComponent("Hi ViralCrew, ich interessiere mich für eure TikTok Shop Agentur.");
const DISCORD_URL = "https://discord.gg/viralcrew";

function AudienceToggle({ value, onChange }) {
  const { t } = useI18n();
  return (
    <div className="inline-flex bg-zinc-900/80 border border-white/10 rounded-full p-1">
      {["creator", "brand"].map((v) => (
        <button
          key={v}
          data-testid={v === "creator" ? HOME.audienceToggleCreator : HOME.audienceToggleBrand}
          onClick={() => onChange(v)}
          className={`relative z-10 px-5 py-2 text-sm font-semibold rounded-full transition-all ${
            value === v ? "bg-[#FF0050] text-white shadow-[0_0_24px_rgba(255,0,80,0.5)]" : "text-zinc-400 hover:text-white"
          }`}
        >
          {v === "creator" ? t.audience.creator : t.audience.brand}
        </button>
      ))}
    </div>
  );
}

function Hero({ audience, setAudience }) {
  const { t } = useI18n();
  const title = audience === "creator" ? t.hero.creatorTitle : t.hero.brandTitle;
  const sub = audience === "creator" ? t.hero.creatorSub : t.hero.brandSub;
  const cta = audience === "creator" ? t.hero.ctaCreator : t.hero.ctaBrand;
  return (
    <section className="relative overflow-hidden grain">
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-32 -left-20 w-[600px] h-[600px] bg-[#FF0050] opacity-20 blur-[150px] rounded-full" />
        <div className="absolute top-40 right-0 w-[500px] h-[500px] bg-[#00F2FE] opacity-10 blur-[150px] rounded-full" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(255,0,80,0.08),transparent_60%)]" />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-16 md:pb-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex flex-wrap items-center gap-2 mb-6">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/30 text-[#FF0050] text-xs font-mono uppercase tracking-wider">
            <Sparkles className="h-3 w-3" />{t.hero.eyebrow}
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/15 text-white text-xs font-mono uppercase tracking-wider" data-testid="tiktok-partner-badge">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.59a8.16 8.16 0 0 0 4.77 1.52V6.69h-1.84Z"/></svg>
            Offizieller TikTok Shop Partner
          </span>
        </motion.div>
        <motion.h1
          key={audience + title}
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="font-display font-black tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.95] mb-6 whitespace-pre-line max-w-5xl"
        >
          {title.split("\n").map((line, i) => (
            <span key={i} className="block">
              {i === 1 ? <span className="gradient-text">{line}</span> : line}
            </span>
          ))}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="text-zinc-400 text-lg md:text-xl max-w-2xl mb-8 leading-relaxed">{sub}</motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-10">
          <AudienceToggle value={audience} onChange={setAudience} />
          <a href="#contact" data-testid={HOME.heroCtaPrimary} className="group inline-flex items-center gap-2 bg-[#FF0050] hover:bg-[#E00046] text-white font-bold rounded-full px-7 py-3.5 transition-all hover:shadow-[0_0_30px_rgba(255,0,80,0.6)]">
            {cta}<ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a href="#features" data-testid={HOME.heroCtaSecondary} className="inline-flex items-center gap-2 text-white font-medium rounded-full px-5 py-3 border border-white/15 hover:border-white/40 transition">{t.hero.ctaSecondary}</a>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
          {[
            { v: "€6M+", l: t.metrics.gmv },
            { v: "70+", l: t.metrics.creators },
            { v: "40+", l: t.metrics.campaigns },
            { v: "92%", l: t.metrics.retention },
          ].map((m) => (
            <div key={m.l} className="bg-[#0A0A0A] p-6 md:p-8">
              <div className="font-display font-black text-3xl md:text-4xl tracking-tighter">{m.v}</div>
              <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mt-2">{m.l}</div>
            </div>
          ))}
        </motion.div>
      </div>
      <div className="border-y border-white/10 py-6 overflow-hidden bg-black">
        <p className="text-center text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-600 mb-3">{t.marqueeLabel}</p>
        <div className="flex gap-16 animate-marquee whitespace-nowrap font-display font-black tracking-tighter text-2xl md:text-3xl text-zinc-700">
          {t.marquee.concat(t.marquee).map((b, i) => (
            <span key={i} className="hover:text-white transition">{b}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  const { t } = useI18n();
  const items = [
    { icon: TrendingUp, title: t.features.trendsTitle, desc: t.features.trendsDesc, color: "text-[#00F2FE]" },
    { icon: Gift, title: t.features.samplesTitle, desc: t.features.samplesDesc, color: "text-[#FF0050]" },
    { icon: Package, title: t.features.dealsTitle, desc: t.features.dealsDesc, color: "text-[#9D4CDD]" },
    { icon: BarChart3, title: t.features.analyticsTitle, desc: t.features.analyticsDesc, color: "text-[#00F2FE]" },
    { icon: UserCog, title: t.features.managerTitle, desc: t.features.managerDesc, color: "text-[#FF0050]" },
    { icon: Users, title: t.features.networkTitle, desc: t.features.networkDesc, color: "text-[#9D4CDD]" },
  ];
  return (
    <section id="features" className="relative py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-16">
          <span className="inline-block text-xs font-mono uppercase tracking-wider text-[#00F2FE] mb-4">// Features</span>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[0.95] whitespace-pre-line mb-5">{t.features.title}</h2>
          <p className="text-zinc-400 text-lg">{t.features.subtitle}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 rounded-3xl overflow-hidden border border-white/10">
          {items.map((it, i) => (
            <motion.div key={it.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }} className="bg-[#0A0A0A] p-8 hover:bg-[#101012] transition group">
              <div className={`h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center mb-5 ${it.color} group-hover:scale-110 transition-transform`}>
                <it.icon className="h-5 w-5" />
              </div>
              <h3 className="font-display font-bold text-xl mb-2">{it.title}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{it.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingCell({ value }) {
  if (value === true) return <Check className="h-4 w-4 text-[#FF0050]" />;
  if (value === false) return <X className="h-4 w-4 text-zinc-700" />;
  return <span className="text-sm text-zinc-200">{value}</span>;
}

function Pricing() {
  const { t } = useI18n();
  return (
    <section id="pricing" className="relative py-16 md:py-20 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[#FF0050] opacity-[0.08] blur-[150px] rounded-full" />
      </div>
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-mono uppercase tracking-wider text-[#FF0050] mb-4">// Pricing</span>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl mb-4">{t.pricing.title}</h2>
          <p className="text-zinc-400 text-lg">{t.pricing.subtitle}</p>
        </div>
        <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden hidden md:block">
          <div className="grid grid-cols-[1.4fr_1fr_1fr]">
            <div className="p-6 border-b border-white/10" />
            <div className="p-6 border-b border-l border-white/10">
              <div className="font-display font-bold text-xl">{t.pricing.free}</div>
              <div className="font-display font-black text-3xl tracking-tighter mt-2">{t.pricing.freeAmount}</div>
            </div>
            <div className="relative p-6 border-b border-l border-white/10 bg-gradient-to-b from-[#1a0a12] to-transparent">
              <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF0050] text-white text-[10px] font-mono uppercase tracking-wider font-bold">
                <Zap className="h-3 w-3" />{t.pricing.mostPopular}
              </div>
              <div className="font-display font-bold text-xl text-glow-pink">{t.pricing.pro}</div>
              <div className="font-display font-black text-3xl tracking-tighter mt-2">{t.pricing.proAmount}<span className="text-sm text-zinc-500 font-medium">{t.pricing.perMonth}</span></div>
            </div>
            {t.pricing.rows.map((r, i) => (
              <React.Fragment key={i}>
                <div className="p-4 border-b border-white/5 text-sm text-zinc-300">{r.label}</div>
                <div className="p-4 border-b border-l border-white/5 flex items-center"><PricingCell value={r.free} /></div>
                <div className="p-4 border-b border-l border-white/5 bg-[#0c0a0e] flex items-center"><PricingCell value={r.pro} /></div>
              </React.Fragment>
            ))}
            <div className="p-6" />
            <div className="p-6 border-l border-white/10">
              <a href={WHOP_FREE_URL} target="_blank" rel="noopener noreferrer" data-testid={HOME.pricingFreeCta} className="block text-center bg-transparent border-2 border-zinc-800 text-white font-semibold rounded-full px-5 py-2.5 hover:border-zinc-500 transition text-sm">{t.pricing.freeCta}</a>
            </div>
            <div className="p-6 border-l border-white/10 bg-[#0c0a0e]">
              <a href={WHOP_PRO_URL} target="_blank" rel="noopener noreferrer" data-testid={HOME.pricingProCta} className="block text-center bg-[#FF0050] hover:bg-[#E00046] text-white font-bold rounded-full px-5 py-2.5 transition-all hover:shadow-[0_0_30px_rgba(255,0,80,0.7)] text-sm">{t.pricing.proCta} <ArrowUpRight className="inline h-3.5 w-3.5" /></a>
            </div>
          </div>
        </div>

        <div className="md:hidden space-y-5">
          <PlanCardMobile planKey="free" name={t.pricing.free} amount={t.pricing.freeAmount} cta={t.pricing.freeCta} href={WHOP_FREE_URL} testid="pricing-free-cta-mobile" />
          <PlanCardMobile planKey="pro" name={t.pricing.pro} amount={t.pricing.proAmount} per={t.pricing.perMonth} cta={t.pricing.proCta} href={WHOP_PRO_URL} testid="pricing-pro-cta-mobile" highlight badge={t.pricing.mostPopular} />
        </div>
      </div>
    </section>
  );
}

function PlanCardMobile({ planKey, name, amount, per, cta, href, testid, highlight, badge }) {
  const { t } = useI18n();
  return (
    <div data-testid={`plan-card-${planKey}`} className={`relative rounded-3xl border p-6 ${highlight ? "border-[#FF0050]/40 bg-gradient-to-b from-[#1a0a12] to-[#0A0A0A]" : "border-white/10 bg-[#0A0A0A]"}`}>
      {badge && (
        <div className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF0050] text-white text-[10px] font-mono uppercase tracking-wider font-bold">
          <Zap className="h-3 w-3" />{badge}
        </div>
      )}
      <div className="font-display font-bold text-xl mb-1">{name}</div>
      <div className="font-display font-black text-3xl tracking-tighter mb-5">{amount}{per && <span className="text-sm text-zinc-500 font-medium">{per}</span>}</div>
      <ul className="mb-6">
        {t.pricing.rows.map((r, i) => {
          const val = r[planKey];
          return (
            <li key={i} className="flex items-start justify-between gap-3 border-t border-white/5 py-2.5">
              <span className="text-sm text-zinc-400">{r.label}</span>
              <span className="shrink-0 text-right">
                {val === true ? <Check className="h-4 w-4 text-[#FF0050] inline" /> : val === false ? <X className="h-4 w-4 text-zinc-700 inline" /> : <span className="text-sm text-zinc-200">{val}</span>}
              </span>
            </li>
          );
        })}
      </ul>
      <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
        className={`block text-center font-bold rounded-full px-5 py-3 text-sm transition ${highlight ? "bg-[#FF0050] hover:bg-[#E00046] text-white hover:shadow-[0_0_30px_rgba(255,0,80,0.6)]" : "border-2 border-zinc-800 text-white hover:border-zinc-500"}`}>
        {cta}{highlight && <ArrowUpRight className="inline h-3.5 w-3.5 ml-1" />}
      </a>
    </div>
  );
}

function ContactSection({ audience }) {
  const { t } = useI18n();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, type: audience });
      toast.success(t.contact.success);
      setForm({});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section id="contact" className="relative py-16 md:py-20 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-4">{t.contact.title}</h2>
          <p className="text-zinc-400">{t.contact.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="group rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-[#25D366]/50 transition">
            <div className="h-10 w-10 rounded-lg bg-[#25D366]/10 border border-[#25D366]/30 flex items-center justify-center mb-4 text-[#25D366]">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div className="font-display font-bold text-lg mb-1">{t.contact.whatsapp}</div>
            <div className="text-zinc-500 text-sm font-mono">{WHATSAPP_NUMBER_RAW}</div>
            <div className="text-xs text-zinc-600 mt-2">{t.contact.whatsappSub}</div>
          </a>
          <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-discord" className="group rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-[#5865F2]/50 transition">
            <div className="h-10 w-10 rounded-lg bg-[#5865F2]/10 border border-[#5865F2]/30 flex items-center justify-center mb-4 text-[#5865F2]">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.078.078 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
            </div>
            <div className="font-display font-bold text-lg mb-1">{t.contact.discord}</div>
            <div className="text-zinc-500 text-sm font-mono">discord.gg/viralcrew</div>
            <div className="text-xs text-zinc-600 mt-2">{t.contact.discordSub}</div>
          </a>
          <a href={WHOP_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-whop" className="group rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-[#FF0050]/50 transition">
            <div className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center mb-4 p-1.5">
              <img src={WHOP_LOGO} alt="Whop" className="h-full w-full object-contain" />
            </div>
            <div className="font-display font-bold text-lg mb-1">{t.contact.whop}</div>
            <div className="text-zinc-500 text-sm font-mono">whop.com/viralcrewtiktok</div>
            <div className="text-xs text-zinc-600 mt-2">{t.contact.whopSub}</div>
          </a>
        </div>

        <form onSubmit={onSubmit} className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 md:p-10 space-y-4">
          <h3 className="font-display font-bold text-xl mb-2">{t.contact.formTitle}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <input required placeholder={t.contact.name} value={form.name || ""} onChange={set("name")} data-testid={HOME.leadFormName}
              className="bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00F2FE] transition" />
            <input required type="email" placeholder={t.contact.email} value={form.email || ""} onChange={set("email")} data-testid={HOME.leadFormEmail}
              className="bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00F2FE] transition" />
          </div>
          <input required placeholder={t.contact.handle} value={form.handle || ""} onChange={set("handle")} data-testid={HOME.leadFormHandle}
            className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00F2FE] transition" />
          <div className="grid md:grid-cols-2 gap-4">
            <input placeholder={t.contact.phone} value={form.phone || ""} onChange={set("phone")} data-testid="lead-form-phone"
              className="bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00F2FE] transition" />
            <input placeholder={t.contact.mobile} value={form.mobile || ""} onChange={set("mobile")} data-testid="lead-form-mobile"
              className="bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00F2FE] transition" />
          </div>
          <textarea required rows={4} placeholder={t.contact.message} value={form.message || ""} onChange={set("message")} data-testid={HOME.leadFormMessage}
            className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00F2FE] transition" />
          <button type="submit" disabled={loading} data-testid={HOME.leadFormSubmit}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#FF0050] hover:bg-[#E00046] disabled:opacity-60 text-white font-bold rounded-full px-6 py-3.5 transition-all hover:shadow-[0_0_30px_rgba(255,0,80,0.6)]">
            <Send className="h-4 w-4" />{loading ? t.contact.sending : t.contact.submit}
          </button>
        </form>
      </div>
    </section>
  );
}

function FAQ() {
  const { t } = useI18n();
  const [tab, setTab] = useState("creator");
  const items = tab === "creator" ? t.faq.creator : t.faq.brand;
  return (
    <section id="faq" className="py-16 md:py-20 border-t border-white/5">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <div className="text-center mb-10">
          <span className="inline-block text-xs font-mono uppercase tracking-wider text-[#9D4CDD] mb-4">// FAQ</span>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl">{t.faq.title}</h2>
        </div>
        <div className="inline-flex bg-zinc-900/80 border border-white/10 rounded-full p-1 mb-8 mx-auto block w-fit">
          {["creator", "brand"].map((v) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`faq-tab-${v}`}
              className={`px-5 py-2 text-sm font-semibold rounded-full transition-all ${tab === v ? "bg-[#FF0050] text-white shadow-[0_0_24px_rgba(255,0,80,0.4)]" : "text-zinc-400 hover:text-white"}`}>
              {t.faq.tabs[v]}
            </button>
          ))}
        </div>
        <Accordion type="single" collapsible className="w-full">
          {items.map((item, i) => (
            <AccordionItem key={`${tab}-${i}`} value={`item-${i}`} className="border-white/10" data-testid={HOME.faqItem(i)}>
              <AccordionTrigger className="text-left text-white font-display text-lg hover:no-underline hover:text-[#FF0050]">{item.q}</AccordionTrigger>
              <AccordionContent className="text-zinc-400 text-base leading-relaxed">{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function FloatingButtons() {
  const items = [
    {
      href: WHATSAPP_URL, label: "WhatsApp", testid: "floating-whatsapp",
      bg: "bg-[#25D366]",
      icon: <MessageCircle className="h-6 w-6 text-white" />,
    },
    {
      href: DISCORD_URL, label: "Discord", testid: "floating-discord",
      bg: "bg-[#5865F2]",
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" className="text-white"><path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.078.078 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
      ),
    },
    {
      href: WHOP_URL, label: "Whop", testid: "floating-whop",
      bg: "bg-[#FA4517]",
      icon: <img src={WHOP_LOGO} alt="Whop" className="h-full w-full object-cover scale-110" />,
    },
  ];
  return (
    <div className="fixed bottom-5 left-5 z-40 flex flex-col gap-3" data-testid="floating-buttons">
      {items.map((it) => (
        <a key={it.label} href={it.href} target="_blank" rel="noopener noreferrer" aria-label={it.label} data-testid={it.testid}
          className={`h-12 w-12 rounded-full overflow-hidden ${it.bg} flex items-center justify-center shadow-lg hover:scale-110 transition`}>
          {it.icon}
        </a>
      ))}
    </div>
  );
}

export default function LandingPage() {
  const [audience, setAudience] = useState("creator");
  useEffect(() => {
    const hash = window.location.hash?.replace("#", "");
    if (hash) {
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 120);
    }
  }, []);
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <Hero audience={audience} setAudience={setAudience} />
      <ResultsSlider />
      <Features />
      <Pricing />
      <ContactSection audience={audience} />
      <FAQ />
      <Footer />
      <FloatingButtons />
    </div>
  );
}
